import {
  PremedicationOrder,
  PremedicationRoute,
  PremedicationTiming,
  PremedicationType
} from './premedication.model';

export const PREMEDICATION_I18N_PREFIX =
  'doctor-dashboard.details.premedication';

/**
 * Canonical chronological order of timing slots — matches the
 * medical-synthesis-service enum order, so the in-app summary lines render
 * timings in the same order as the (French) Complete Report.
 */
export const PREMED_TIMING_ORDER: PremedicationTiming[] = [
  PremedicationTiming.VEILLE,
  PremedicationTiming.MORNING,
  PremedicationTiming.H2,
  PremedicationTiming.H1,
  PremedicationTiming.BLOCK,
  PremedicationTiming.RECOVERY,
  PremedicationTiming.POSTOP
];

export const PREMED_ROUTE_ORDER: PremedicationRoute[] = [
  PremedicationRoute.ORAL,
  PremedicationRoute.IV,
  PremedicationRoute.SC,
  PremedicationRoute.IM,
  PremedicationRoute.CUTANEOUS,
  PremedicationRoute.INHALED,
  PremedicationRoute.RECTAL
];

/**
 * Translation-key builders for the timing/route clinical values. Labels are
 * resolved through the shared `doctor-dashboard.*` dictionary so the in-app
 * summary and the (server-rendered) Complete Report draw from the same keys —
 * see CompleteReportGenerator in medical-synthesis-service. The French values
 * for these keys must match the report's, so the FR summary stays in parity.
 */
export type PremedTranslate = (id: string) => string;

export const premedTimingLabelId = (timing: PremedicationTiming): string =>
  `${PREMEDICATION_I18N_PREFIX}.timing.${timing}`;

export const premedRouteLabelId = (route: PremedicationRoute): string =>
  `${PREMEDICATION_I18N_PREFIX}.route.${route}`;

/** Translation key for the localized chrome label of a premedication type. */
export const premedTypeLabelId = (type: PremedicationType): string =>
  `${PREMEDICATION_I18N_PREFIX}.type.${type}`;

/**
 * The `medical_synthesis.*` namespace under which built-in care acts are keyed —
 * mirrors the medication-name convention (clinical value names the report
 * localizes live in medical_synthesis, vs the doctor-dashboard UI chrome).
 */
const PREMED_CARE_OPTION_PREFIX = 'medical_synthesis.premedication.careOption.';

/**
 * Static 10-item care-act seed list for the free-solo "care name" autocomplete.
 * Each option is the full `medical_synthesis.*` translation key that is stored
 * verbatim as the order `name` when selected, so the report and the app localize
 * it via the {@link premedDisplayName} / nameTranslatable convention. Free text is
 * still allowed and stored as-is. A fixed frontend constant, NOT a config library.
 */
export const PREMED_CARE_OPTIONS: string[] = [
  'bgl',
  'ivLine',
  'compression',
  'antisepticShower',
  'urinaryCatheter',
  'ngTube',
  'nebulization',
  'fastingCheck',
  'bowelPrep',
  'hairClipping'
].map((key) => `${PREMED_CARE_OPTION_PREFIX}${key}`);

/**
 * Resolves a stored order `name` to display text: built-in care options are kept
 * as `medical_synthesis.*` translation keys and localized in the active locale;
 * free text is shown verbatim. Prefers the backend `nameTranslatable` flag,
 * falling back to options membership for unsaved drafts. Mirrors the medication
 * nameTranslatable rule.
 */
export const premedDisplayName = (
  order: { name?: string; nameTranslatable?: boolean },
  translate: PremedTranslate
): string => {
  const name = order.name ?? '';
  const translatable =
    order.nameTranslatable ?? PREMED_CARE_OPTIONS.includes(name);
  return translatable && name ? translate(name) : name;
};

const sortPremedTimings = (
  timings: PremedicationTiming[] = []
): PremedicationTiming[] =>
  [...timings].sort(
    (a, b) => PREMED_TIMING_ORDER.indexOf(a) - PREMED_TIMING_ORDER.indexOf(b)
  );

/** Comma + "and"-joined, locale-aware timing list (e.g. "le matin et H-1"). */
export const joinPremedTimings = (
  timings: PremedicationTiming[] | undefined,
  translate: PremedTranslate
): string => {
  const labels = sortPremedTimings(timings).map((timing) =>
    translate(premedTimingLabelId(timing))
  );
  if (labels.length === 0) {
    return '';
  }
  if (labels.length === 1) {
    return labels[0];
  }
  const and = translate(`${PREMEDICATION_I18N_PREFIX}.summary.and`);
  return `${labels.slice(0, -1).join(', ')} ${and} ${
    labels[labels.length - 1]
  }`;
};

/** ` (si besoin : condition)` suffix, empty when the order is not if-needed. */
const premedIfNeededSuffix = (
  order: Partial<PremedicationOrder>,
  translate: PremedTranslate
): string => {
  if (!order.ifNeeded) {
    return '';
  }
  const ifNeeded = translate(`${PREMEDICATION_I18N_PREFIX}.summary.ifNeeded`);
  return order.condition
    ? ` (${ifNeeded} : ${order.condition})`
    : ` (${ifNeeded})`;
};

/**
 * Builds the summary line for an order in the active UI locale. Stays in parity
 * with the (server-rendered) Complete Report when both resolve the same
 * dictionary keys (medical-synthesis CompleteReportGenerator.toPremedicationLine):
 *   treatment: NAME : dose route timing (si besoin : condition) — comment
 *   care act : NAME : timing (si besoin : condition) (comment)
 */
export const buildPremedSummary = (
  order: Partial<PremedicationOrder>,
  translate: PremedTranslate
): string => {
  const timings = joinPremedTimings(order.timings, translate);
  const name = premedDisplayName(order, translate);

  if (order.type === PremedicationType.CARE) {
    const base =
      `${name} : ${timings}`.trimEnd() + premedIfNeededSuffix(order, translate);
    return order.comment ? `${base} (${order.comment})` : base;
  }

  const doseAndRoute = [
    order.dosage,
    order.route ? translate(premedRouteLabelId(order.route)) : ''
  ]
    .filter(Boolean)
    .join(' ');
  let summary = `${name} : ${[doseAndRoute, timings]
    .filter(Boolean)
    .join(' ')}`.trimEnd();
  summary += premedIfNeededSuffix(order, translate);
  if (order.comment) {
    summary += ` — ${order.comment}`;
  }
  return summary;
};
